import { useId, useMemo, useState, type ReactNode } from 'react'
import { ArrowLeftRight, ArrowRight, Minus, Plus, RotateCcw, UserRound, Zap, type LucideIcon } from 'lucide-react'
import type { ShiftOutcomeKind } from '../../partner/index.js'
import { MonthStepper } from './MonthStepper.js'
import { ScheduleDayDetails, formatDay, type DayEnrollment } from './ScheduleDayDetails.js'
import { buildScheduleMonth, dayHasDetails, scheduleRange, shiftMonth, todayDate, type ScheduleDay, type ScheduleRecords } from './scheduleCalendarData.js'
import { ORIGIN_LABEL, REMOVED_LABEL, unitShort, type DayMarkKind } from './scheduleDayMarks.js'

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const

/** How a day with one of the worker's own shifts is filled: the outcome that counts most against wins. */
const OUTCOME_FILL: Record<ShiftOutcomeKind, string> = {
  completed: 'bg-green text-white',
  callout: 'bg-coral-lt text-coral',
  no_show: 'bg-coral text-white',
}
const OUTCOME_RANK: readonly ShiftOutcomeKind[] = ['no_show', 'callout', 'completed']

/** What fills a day: whose it is (worker calendar design, 26 October 2026). */
const LEGEND: readonly { label: string; swatch: string }[] = [
  { label: 'Yours', swatch: 'bg-marine' },
  { label: 'Your draft', swatch: 'bg-marine/15 border border-dashed border-marine' },
  { label: 'Worked', swatch: 'bg-green' },
  { label: 'Call-out', swatch: 'bg-coral-lt border border-coral/40' },
  { label: 'No-show', swatch: 'bg-coral' },
  { label: 'Offered to you', swatch: 'bg-white border-2 border-coral' },
  { label: 'Open shifts', swatch: 'bg-white border-2 border-marine' },
  { label: 'Taken off', swatch: 'bg-white border-[1.5px] border-coral/50' },
  { label: 'Went to someone ahead', swatch: 'bg-coral-lt border border-coral/30' },
  { label: 'PTO', swatch: 'bg-gold-lt border border-gold' },
  { label: 'Bridge on call', swatch: 'bg-white border-2 border-dashed border-marine' },
  { label: 'Holiday event', swatch: 'bg-gold/30 border border-gold' },
]

/** The corner marks: how a day got there after the release. */
const MARK_ICONS: Record<Exclude<DayMarkKind, 'against'>, LucideIcon> = {
  picked_up: Plus,
  assigned: UserRound,
  swapped_in: ArrowLeftRight,
  moved: ArrowRight,
  recalled: RotateCcw,
  bridge: Zap,
}
const MARK_LEGEND: readonly { label: string; kind: DayMarkKind | 'removed' }[] = [
  { label: 'Picked up', kind: 'picked_up' },
  { label: 'By your supervisor', kind: 'assigned' },
  { label: 'Swapped in', kind: 'swapped_in' },
  { label: 'Moved', kind: 'moved' },
  { label: 'Taken back', kind: 'recalled' },
  { label: 'Clinical Bridge', kind: 'bridge' },
  { label: 'Against your ask', kind: 'against' },
  { label: 'Taken off', kind: 'removed' },
]

/** One corner mark: marine for how a day came to the worker, coral for what went against them. */
export function DayCornerMark({ kind, coral, className = 'absolute -right-1.5 -top-1.5' }: { kind: DayMarkKind | 'removed'; coral: boolean; className?: string }) {
  const Icon = kind === 'removed' ? Minus : kind === 'against' ? null : MARK_ICONS[kind]
  return (
    <span aria-hidden="true" className={`${className} flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] bg-white ${coral ? 'border-coral text-coral' : 'border-marine text-marine'}`}>
      {Icon ? <Icon className="h-2.5 w-2.5" strokeWidth={3} /> : <span className="text-[9px] font-black leading-none">!</span>}
    </span>
  )
}

/** The mark a day wears, if any: its origin on a day the worker holds, a shift taken off, else an on-call Bridge day. */
function markOf(day: ScheduleDay): { kind: DayMarkKind | 'removed'; coral: boolean } | null {
  const holds = day.scheduled !== null || day.outcomes.length > 0
  if (holds && day.mark?.origin) {
    return { kind: day.mark.origin.kind, coral: day.mark.origin.kind === 'against' }
  }
  if (!holds && day.mark?.removed) {
    return { kind: day.mark.removed.offeredBack ? 'recalled' : 'removed', coral: true }
  }
  return !holds && day.bridge ? { kind: 'bridge', coral: false } : null
}

function dayClass(day: ScheduleDay): string {
  const worst = OUTCOME_RANK.find((kind) => day.outcomes.some((outcome) => outcome.kind === kind))
  if (worst) {
    return OUTCOME_FILL[worst]
  }
  // A shift the worker holds, however it came to them: solid. The corner mark says how.
  if (day.scheduled) {
    return 'bg-marine text-white'
  }
  if (day.mark?.removed) {
    return 'bg-white text-ink-mid border-[1.5px] border-coral/50'
  }
  // The one kind that asks for an answer.
  if (day.mark?.offered) {
    return 'bg-white text-ink border-2 border-coral'
  }
  // A shift on the worker's draft of a month not yet released: theirs to ask for, not yet theirs - light and dashed.
  if (day.drafted) {
    return 'bg-marine/15 text-marine-dk border border-dashed border-marine'
  }
  if (day.mark?.pto === 'approved') {
    return 'bg-gold-lt text-ink border border-gold'
  }
  if (day.mark?.pto === 'requested') {
    return 'bg-gold-lt text-ink border border-dashed border-gold'
  }
  if (day.mark?.lost) {
    return 'bg-coral-lt text-ink-mid border border-coral/30'
  }
  if (day.bridge) {
    return 'bg-white text-ink border-2 border-dashed border-marine'
  }
  if (day.available.length > 0) {
    return 'bg-white text-ink border-2 border-marine'
  }
  return day.holiday ? 'bg-gold/30 text-ink border border-gold' : 'text-ink-mid/70'
}

/** A shift type as the day's label: "eve" for evening, the rest as they are. */
function shortType(shiftType: string): string {
  return shiftType === 'evening' ? 'eve' : shiftType
}

function describeDay(day: ScheduleDay, enrollable: boolean, unitNames: ReadonlyMap<string, string>): string {
  const mark = day.mark
  const parts = [
    ...(mark?.origin ? [mark.origin.note ?? ORIGIN_LABEL[mark.origin.kind].toLowerCase()] : []),
    ...(mark?.awayUnitId ? [`on ${unitNames.get(mark.awayUnitId) ?? mark.awayUnitId}`] : []),
    ...(mark?.removed ? [`${REMOVED_LABEL[mark.removed.kind].toLowerCase()}: ${mark.removed.shiftType}${mark.removed.offeredBack ? ', offered back to you' : ''}`] : []),
    ...(mark?.offered ? ['a shift offered to you, waiting for your answer'] : []),
    ...(mark?.lost ? ['asked for; went to someone ranked ahead'] : []),
    ...(mark?.pto ? [`PTO ${mark.pto}`] : []),
    ...day.outcomes.map((outcome) => (outcome.kind === 'completed' ? 'shift worked' : outcome.kind === 'callout' ? 'call-out' : 'no-show')),
    ...(day.scheduled && day.outcomes.length === 0 ? [`scheduled: ${day.scheduled.shiftType} ${day.scheduled.startTime}–${day.scheduled.endTime}`] : []),
    ...(day.drafted && !day.scheduled && day.outcomes.length === 0 ? [`on your draft: ${day.drafted.shiftType} ${day.drafted.startTime}–${day.drafted.endTime}`] : []),
    ...(day.available.length > 0 ? [`${day.available.length} open ${day.available.length === 1 ? 'shift' : 'shifts'}`] : []),
    ...(day.bridge ? ['enrolled for Clinical Bridge'] : enrollable ? ['can enroll for Clinical Bridge'] : []),
    ...(day.holiday ? [`holiday event: ${day.holiday.name}`] : []),
  ]
  return `${formatDay(day.date)}: ${parts.length > 0 ? parts.join(', ') : 'nothing scheduled'}`
}

interface ScheduleCalendarProps {
  records: ScheduleRecords
  /** Set when the worker may enroll for Clinical Bridge days: then a future date opens its details even with nothing on it. */
  enrollment?: DayEnrollment | null
  /** The month (`YYYY-MM`) to open on; the current month when absent. */
  initialMonth?: string
  /** Told the month shown whenever the arrows change it, so the page can fetch what that month needs (a past month's released schedule). */
  onMonthChange?: (month: string) => void
  /** Above the grid, for the month shown: the released month's counts, say. */
  header?: (month: string) => ReactNode
  /** Under the legend, for the month shown: what changed since the release, say. */
  footer?: (month: string) => ReactNode
}

/**
 * The worker's shifts and schedule as one calendar, a month at a time:
 * the shifts they have had (filled by how each went), the facility's open
 * shifts, their draft of a month not yet released, their Clinical Bridge
 * days and any holiday event. A date with
 * anything on it - or one the worker could enroll for - is a button that
 * opens its details over the page. Opens on the current month; the arrows
 * move between the months that have anything.
 */
export function ScheduleCalendar({ records, enrollment = null, initialMonth, onMonthChange, header, footer }: ScheduleCalendarProps) {
  const unitNames = records.unitNames ?? new Map<string, string>()
  const today = todayDate()
  const range = useMemo(() => scheduleRange(records, today), [records, today])
  const [month, setMonth] = useState(initialMonth ?? today.slice(0, 7))
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const calendar = useMemo(() => buildScheduleMonth(month, records, today), [month, records, today])
  const selected = calendar.days.find((day) => day.date === selectedDate) ?? null
  const dialogId = `${useId()}-day`
  const enrollable = (date: string): boolean => enrollment?.enrollable(date) ?? false
  // A worker who can enroll may page to next month even when nothing is on it yet.
  const lastMonth = enrollment ? [range.last, shiftMonth(today.slice(0, 7), 1)].sort().at(-1) ?? range.last : range.last

  return (
    <div>
      <div className="mb-2 flex justify-center">
        <MonthStepper value={month} min={range.first} max={lastMonth} labelOf={() => calendar.label} onChange={(next) => { setSelectedDate(null); setMonth(next); onMonthChange?.(next) }} />
      </div>

      {header?.(month)}

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAY_INITIALS.map((initial, index) => (
          <div key={index} className="text-[9px] font-bold text-ink-mid">
            {initial}
          </div>
        ))}
        {Array.from({ length: calendar.leadingBlanks }, (_, index) => (
          <div key={`blank-${index}`} aria-hidden="true" />
        ))}
        {calendar.days.map((day) => {
          const canEnrollOn = enrollable(day.date)
          const label = describeDay(day, canEnrollOn, unitNames)
          // The released shift, else the drafted one, labelled by its type until an outcome fills the day.
          const shiftOnDay = day.outcomes.length === 0 ? (day.scheduled ?? day.drafted) : null
          const mark = markOf(day)
          // A shift taken off stays named, struck through, so the change can be seen.
          const removedType = !shiftOnDay && day.outcomes.length === 0 ? (day.mark?.removed?.shiftType ?? null) : null
          const away = day.mark?.awayUnitId && (day.scheduled || day.outcomes.length > 0) ? unitShort(day.mark.awayUnitId, unitNames) : null
          const base = `relative flex h-10 items-center justify-center rounded text-[11px] ${dayClass(day)} ${day.isToday ? 'ring-2 ring-marine-dk ring-offset-1' : ''}`
          if (!dayHasDetails(day, enrollable)) {
            return (
              <div key={day.day} role="img" aria-label={label} className={base}>
                {day.day}
              </div>
            )
          }
          return (
            <button key={day.day} type="button" aria-label={label} aria-haspopup="dialog" onClick={() => setSelectedDate(day.date)} className={`${base} ${canEnrollOn && !day.bridge && day.outcomes.length === 0 && day.available.length === 0 && !day.holiday ? 'text-ink' : 'font-bold'} hover:opacity-80`}>
              {day.day}
              {shiftOnDay && <span aria-hidden="true" className="absolute bottom-0.5 text-[7px] font-bold uppercase leading-none">{shortType(shiftOnDay.shiftType)}</span>}
              {removedType && <span aria-hidden="true" className="absolute bottom-0.5 text-[7px] font-bold uppercase leading-none text-coral line-through">{shortType(removedType)}</span>}
              {!shiftOnDay && !removedType && day.outcomes.length === 0 && day.mark?.pto && <span aria-hidden="true" className="absolute bottom-0.5 text-[7px] font-bold uppercase leading-none text-[#6B5414]">pto</span>}
              {away && <span aria-hidden="true" className="absolute bottom-0.5 left-1 text-[7px] font-black leading-none">{away}</span>}
              {(day.mark?.offered || day.mark?.removed?.offeredBack) && <span aria-hidden="true" className="absolute left-1 top-1 h-1.5 w-1.5 rounded-full bg-coral" />}
              {mark && <DayCornerMark kind={mark.kind} coral={mark.coral} />}
              {/* A day already filled by the worker's own shift still says there are open shifts on it. */}
              {day.outcomes.length > 0 && day.available.length > 0 && <span aria-hidden="true" className="absolute bottom-1 h-1 w-1 rounded-full bg-marine-dk" />}
            </button>
          )
        })}
      </div>

      <ul className="mt-3 grid grid-cols-3 gap-x-2 gap-y-1">
        {LEGEND.map((entry) => (
          <li key={entry.label} className="flex items-center gap-1.5 text-[9.5px] text-ink-mid">
            <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 shrink-0 rounded-sm ${entry.swatch}`} />
            {entry.label}
          </li>
        ))}
      </ul>
      <ul className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1.5" aria-label="Corner marks">
        {MARK_LEGEND.map((entry) => (
          <li key={entry.label} className="flex items-center gap-1.5 text-[9.5px] text-ink-mid">
            <DayCornerMark kind={entry.kind} coral={entry.kind === 'against' || entry.kind === 'removed'} className="relative shrink-0" />
            {entry.label}
          </li>
        ))}
      </ul>

      {footer?.(month)}

      {selected && <ScheduleDayDetails day={selected} id={dialogId} enrollment={enrollment} unitNames={unitNames} onClose={() => setSelectedDate(null)} />}
    </div>
  )
}
